import React from 'react';
import { Check, ChevronDown } from 'lucide-react';

interface RefineOptionTileProps {
  title: string;
  description: React.ReactNode;
  icon: React.ReactNode;
  expanded: boolean;
  appliedLabel?: string;
  appliedHint?: string;
  disabled?: boolean;
  onClick: () => void;
  controls: string;
}

const RefineOptionTile: React.FC<RefineOptionTileProps> = ({
  title,
  description,
  icon,
  expanded,
  appliedLabel,
  appliedHint,
  disabled = false,
  onClick,
  controls,
}) => {
  const applied = Boolean(appliedLabel);

  return (
    <button
      type="button"
      onClick={() => {
        if (!disabled) onClick();
      }}
      aria-expanded={expanded}
      aria-controls={controls}
      aria-disabled={disabled}
      className={`flex min-h-[112px] w-full flex-col gap-2 rounded-xl border p-4 text-left transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8A3033] ${
        disabled
          ? 'cursor-not-allowed border-dashed border-gray-200 bg-gray-50 text-gray-400'
          : applied
            ? 'border-[#8A3033] bg-[#FDF9F9] shadow-sm'
            : expanded
              ? 'border-[#8A3033]/60 bg-white shadow-sm'
              : 'border-gray-200 bg-white hover:border-[#8A3033]/40 hover:shadow-sm'
      }`}
    >
      <span className="flex w-full items-start justify-between gap-3">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
          disabled ? 'bg-gray-100 text-gray-400' : applied ? 'bg-[#8A3033]/10 text-[#8A3033]' : 'bg-gray-100 text-gray-600'
        }`}>
          {icon}
        </span>
        {applied ? (
          <Check className="h-5 w-5 shrink-0 text-[#8A3033]" aria-hidden="true" />
        ) : (
          <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
        )}
      </span>
      <span className={`text-sm font-semibold ${disabled ? 'text-gray-400' : 'text-gray-900'}`}>{title}</span>
      {appliedLabel ? (
        <span className="flex flex-col items-start gap-1">
          <span className="inline-flex rounded-full border border-[#8A3033]/25 bg-white px-2.5 py-1 text-xs font-semibold text-[#8A3033]">
            {appliedLabel}
          </span>
          {appliedHint && (
            <span className="text-[11px] leading-snug text-gray-400">{appliedHint}</span>
          )}
        </span>
      ) : (
        <span className={`text-xs leading-relaxed ${disabled ? 'text-gray-400' : 'text-gray-500'}`}>{description}</span>
      )}
    </button>
  );
};

export default RefineOptionTile;
